import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ReservationService } from '../../../service/reservation-service/reservation.service';
import { SharedDataService } from '../../../service/shared-service/shared-data.service';
import { PatientService } from '../../../service/patient-service/patient.service';
import { decryptField } from '../../../utils/decrypt-utils';
import { sweetAlert } from '../../../utils/swal';
import { ToastrService } from 'ngx-toastr';
import { getReservationStatus } from '../../../utils/placement-status';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment-timezone';

@Component({
  selector: 'app-reservation-detail-modal',
  templateUrl: './reservation-detail-modal.component.html',
  styleUrls: ['./reservation-detail-modal.component.css']
})
export class ReservationDetailModalComponent implements OnInit, OnDestroy {
  @Input() reservationId: number | string;
  @Output() onCloseModal = new EventEmitter<void>();

  reservation: any = null;
  isLoading: boolean = true;
  isSubmitting: boolean = false;
  errorMessage: string = '';

  reservationSummary: any = {};
  patientInfo: any = {};
  agentInfo: any = {};
  facilityInfo: any = {};
  reservationNotes: string = '';
  isUploading: boolean = false;
  uploadedDocuments: any[] = [];
  messages: any[] = [];
  newMessageText: string = '';
  isLoadingNotes: boolean = false;

  levelOfCareMap: { [key: number]: string } = {
    1: 'Assisted Living',
    2: 'Board & Care',
    3: 'Memory Care',
    4: 'Independent Living',
    5: 'Skilled Nursing'
  };

  specialCareMap: { [key: number]: string } = {
    1: 'Oxygen',
    2: 'Catheter',
    3: 'Catheter (Self)',
    4: 'Bedsore',
    5: 'Colostomy',
    6: 'Colostomy (Self)'
  };

  dietRestrictionMap: { [key: number]: string } = {
    1: 'Diabetic',
    2: 'Low Salt',
    3: 'Renal',
    4: 'Puree'
  };

  entitlementMap: { [key: number]: string } = {
    1: 'Medi-Cal',
    2: 'ALWPP',
    3: 'Medi-Cal Pending',
    4: 'VA'
  };

  preIntakeData: any = null;
  showRequestModal = false;
  requestForm: FormGroup;
  submitted = false;

  showRatingModal = false;
  selectedFacilityId: number | string = null;
  selectedReferenceId: string = null;
  selectedFacilityCard: any = null;

  constructor(
    private reservationService: ReservationService,
    private sharedDataService: SharedDataService,
    private patientService: PatientService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private router: Router
  ) { }

  ngOnInit(): void {
    if (this.reservationId) {
      this.fetchReservationDetails();
      this.fetchReservationNotes();
    }
    this.fetchAgentInfo();
    this.initRequestForm();
  }

  ngOnDestroy(): void { }

  closeModal(): void {
    this.showRequestModal = false;
    this.messages = [];
    this.newMessageText = '';
    this.onCloseModal.emit();
  }

  onDeclineRates(): void {
    const intakeId = this.reservation && this.reservation.id;
    if (!intakeId) {
      this.toastr.error('Intake ID is missing.');
      return;
    }

    sweetAlert('Decline Rates', 'Are you sure you want to decline rates?', 'warning', 'Yes', 'No')
      .then((result: any) => {
        if (result && result.value) {
          this.submitDeclineRates(intakeId);
        }
      });
  }

  submitDeclineRates(intakeId: number | string): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.reservationService.declineRates(intakeId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.toastr.success('Rates declined successfully.');
          this.fetchReservationDetails();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error declining rates:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to decline rates.';
          this.toastr.error(errorMsg);
        }
      );
  }

  onCancelReservation(reservationId): void {
    console.log('reservationId', reservationId);
  
    if (!reservationId) {
      this.toastr.error('Onboarding ID is missing.');
      return;
    }

    sweetAlert('Cancel Reservation', 'Are you sure you want to cancel this reservation?', 'warning', 'Yes', 'No')
      .then((result: any) => {
        if (result && result.value) {
          this.submitCancelReservation(reservationId);
        }
      });
  }

  submitCancelReservation(reservationId: number | string): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.reservationService.declineReservation(reservationId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.toastr.success('Reservation cancelled successfully.');
          this.fetchReservationDetails();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error cancelling reservation:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to cancel reservation.';
          this.toastr.error(errorMsg);
        }
      );
  }

  onAcceptRates(): void {
    const intakeId = this.reservation && this.reservation.id;
    if (!intakeId) {
      this.toastr.error('Intake ID is missing.');
      return;
    }

    sweetAlert('Accept Rates', 'Are you sure you want to accept rates?', 'warning', 'Yes', 'No')
      .then((result: any) => {
        if (result && result.value) {
          this.submitAcceptRates(intakeId);
        }
      });
  }

  submitAcceptRates(intakeId: number | string): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.reservationService.acceptRates(intakeId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.toastr.success('Rates accepted successfully.');
          this.fetchReservationDetails();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error accepting rates:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to accept rates.';
          this.toastr.error(errorMsg);
        }
      );
  }

  onFileSelected(event: any): void {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const reservationId = this.reservation && this.reservation.id;
    if (!reservationId) {
      this.toastr.error('Reservation ID is missing.');
      return;
    }

    if (this.isUploading) return;
    this.isUploading = true;

    this.reservationService.uploadPhysicianReport(reservationId, file)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isUploading = false;
          this.toastr.success('Physician report uploaded successfully.');

          this.fetchReservationDetails();
        },
        (error) => {
          this.isUploading = false;
          console.error('Error uploading physician report:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to upload report.';
          this.toastr.error(errorMsg);
        }
      );
  }

  formatDocDetails(doc: any): string {
    const rawDate = doc.created_at || doc.date || doc.createdAt;
    if (rawDate) {
      return moment(rawDate).isValid() ? moment(rawDate).format('MM/DD/YYYY hh:mm A') : String(rawDate);
    }
    return '—';
  }

  formatUploadedDate(rawDate: any): string {
    if (!rawDate) return '—';
    return moment(rawDate).isValid() ? moment(rawDate).format('MMM DD, YYYY') : String(rawDate);
  }

  initRequestForm(): void {
    this.requestForm = this.fb.group({
      requested_to_name: ['', Validators.required],
      requested_to_email: ['', [Validators.required, Validators.email]],
      message: ['', Validators.required]
    });
  }

  get form() {
    return this.requestForm.controls;
  }

  openRequestModal(): void {
    this.submitted = false;
    this.initRequestForm();
    this.showRequestModal = true;
  }

  closeRequestModal(): void {
    this.submitted = false;
    this.showRequestModal = false;
  }

  onRateFacility(): void {
    const facility = this.reservation ? this.reservation.e_facilities : null;
    if (!facility) {
      this.toastr.error('Facility information is missing.');
      return;
    }
    const referenceId = this.reservation ? this.reservation.reference_id : null;
    if (!referenceId) {
      this.toastr.error('Reservation Reference ID is missing.');
      return;
    }
    const img = facility.e_facility_media && facility.e_facility_media[0] ? facility.e_facility_media[0].image : null;
    this.selectedFacilityId = facility.id;
    this.selectedReferenceId = referenceId;
    this.selectedFacilityCard = {
      id: facility.id,
      name: facility.name || '—',
      address: facility.street_address || '—',
      cityName: facility.e_cities ? facility.e_cities.name : '',
      stateName: facility.e_states ? facility.e_states.name : '',
      image: img,
      personCount: facility.capacity || 0,
      bedCount: facility.bedrooms || 0,
      bathCount: facility.bathrooms || 0,
      squareFootage: facility.area || 0
    };
    this.showRatingModal = true;
  }

  closeRatingModal(): void {
    this.showRatingModal = false;
    this.selectedFacilityId = null;
    this.selectedReferenceId = null;
    this.selectedFacilityCard = null;
  }

  hasExistingReview(): boolean {
    if (!this.reservation) {
      return false;
    }
    const review = this.reservation.e_facilityreview;
    if (!review) {
      return false;
    }
    if (Array.isArray(review)) {
      return review.length > 0;
    }
    if (typeof review === 'object') {
      return Object.keys(review).length > 0;
    }
    return true;
  }

  shouldShowRateButton(): boolean {
    if (!this.reservation) {
      return false;
    }
    const statusId = Number(this.reservation.onboarding_status_id);
    const isAdmission = Number(this.reservation.is_admission) === 1;

    const isStatusAllowed = isAdmission || statusId === 7 || statusId === 4;
    return isStatusAllowed && !this.hasExistingReview();
  }

  isAdmissionStatus(): boolean {
    if (!this.reservation) {
      return false;
    }
    return Number(this.reservation.onboarding_status_id) === 4;
  }

  onSendMessage(): void {
    this.onCloseModal.emit();
    this.router.navigate(['/placement/messages'], {
      queryParams: {
        reservationId: this.reservationId
      }
    });
  }

  onSendRequest(): void {
    this.openRequestModal();
  }

  submitFormRequest(): void {
    this.submitted = true;
    if (this.requestForm.invalid) {
      return;
    }

    const onboardingId = this.reservation && this.reservation.id;
    if (!onboardingId) {
      this.toastr.error('Onboarding ID is missing.');
      return;
    }

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const payload = this.requestForm.value;
    const statusId = this.reservation && this.reservation.onboarding_status_id;
    const usePut = Number(statusId) === 9 || Number(statusId) === 10;

    this.reservationService.sendFormRequest(onboardingId, payload, usePut)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.showRequestModal = false;
          this.submitted = false;
          this.toastr.success('Request sent successfully.');
          this.fetchReservationDetails();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error sending form request:', error);
          const errorMsg = error && error.error && error.error.message ? error.error.message : 'Failed to send request.';
          this.toastr.error(errorMsg);
        }
      );
  }

  fetchAgentInfo(): void {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((user) => {
        if (user) {
          const compName = this.formatCompanyName(user.companyName || user.company_name || user.company);
          const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
          this.agentInfo = {
            companyName: compName || '—',
            name: fullName || user.displayName || user.name || user.username || '—',
            phone: user.contactNumber || user.phone || user.cellPhone || '—',
            email: user.email || '—'
          };
        }
      });
  }

  fetchReservationDetails(): void {
    this.isLoading = true;
    this.errorMessage = '';
    forkJoin([
      this.patientService.getPreIntakeData(),
      this.reservationService.getReservationDetails(this.reservationId)
    ]).pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        ([preIntake, res]) => {
          this.isLoading = false;
          this.preIntakeData = preIntake;
          const data = (res && res.data) ? res.data : (res && res.reservation ? res.reservation : res);
          this.reservation = data;
          this.processReservationData(data);
        },
        (error) => {
          console.error('Error fetching reservation details:', error);
          this.isLoading = false;
          this.errorMessage = 'Failed to load reservation details.';
        }
      );
  }

  processReservationData(res: any): void {
    if (!res) return;

    this.uploadedDocuments = [];
    if (res.e_patient602a_form && Array.isArray(res.e_patient602a_form)) {
      this.uploadedDocuments = res.e_patient602a_form;
    } else if (res.e_reservation_forms && Array.isArray(res.e_reservation_forms)) {
      this.uploadedDocuments = res.e_reservation_forms;
    } else if (res.e_onboarding_forms && Array.isArray(res.e_onboarding_forms)) {
      this.uploadedDocuments = res.e_onboarding_forms;
    } else if (res.e_onboarding_documents && Array.isArray(res.e_onboarding_documents)) {
      this.uploadedDocuments = res.e_onboarding_documents;
    } else if (res.forms && Array.isArray(res.forms)) {
      this.uploadedDocuments = res.forms;
    } else if (res.documents && Array.isArray(res.documents)) {
      this.uploadedDocuments = res.documents;
    } else if (res.files && Array.isArray(res.files)) {
      this.uploadedDocuments = res.files;
    }

    // 1. Reservation Summary
    const statusObj = this.getReservationStatus(res.onboarding_status_id, res.e_onboarding_status && res.e_onboarding_status.name);

    this.reservationSummary = {
      referenceNo: res.reference_id || '—',
      facilityTour: res.walkthrough_date ? (moment(res.walkthrough_date).isValid() ? moment(res.walkthrough_date).format('MM/DD/YYYY hh:mm A') : res.walkthrough_date) : '—',
      admission: res.onboarding_date ? (moment(res.onboarding_date).isValid() ? moment(res.onboarding_date).format('MM/DD/YYYY') : res.onboarding_date) : (res.is_admission === 1 ? 'Confirmed' : '—'),
      statusLabel: statusObj.label,
      statusColor: statusObj.color,
      discharge: res.departure_date ? (moment(res.departure_date).isValid() ? moment(res.departure_date).format('MM/DD/YYYY') : res.departure_date) : (res.discharge_reason || '—')
    };

    // 2. Patient Information (Strict source: reservation.e_intakes.e_patients)
    const intake = (res && res.e_intakes) ? (Array.isArray(res.e_intakes) ? res.e_intakes[0] : res.e_intakes) : {};
    const patient = (intake && intake.e_patients) ? (Array.isArray(intake.e_patients) ? intake.e_patients[0] : intake.e_patients) : {};

    const firstName = decryptField(patient.first_name);
    const lastName = decryptField(patient.last_name);
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    const phone = decryptField(patient.phone);
    const rawDob = patient.date_of_birth ? decryptField(patient.date_of_birth) : (patient.dob ? decryptField(patient.dob) : '');
    const formattedDob = rawDob && moment(rawDob).isValid() ? moment(rawDob).format('MM/DD/YYYY') : (rawDob || '—');
    const age = rawDob && moment(rawDob).isValid() ? moment().diff(moment(rawDob), 'years') : null;

    const rawIncome = intake.annual_income ? decryptField(intake.annual_income) : '';
    const formattedIncome = rawIncome ? (isNaN(Number(rawIncome)) ? rawIncome : `$${Number(rawIncome).toLocaleString()}`) : '—';

    // Lists mapping
    const specialCareNeedsStr = this.mapListItems(intake.e_patientintakespecialcare, 'intakespecialcareneeds_id', this.specialCareMap);
    const dietRestrictionsStr = this.mapListItems(intake.e_patientintakedietrestriction, 'intakedietrestrictions_id', this.dietRestrictionMap);
    const entitlementsStr = this.mapListItems(intake.e_patientintakeentitlement, 'intakeentitlements_id', this.entitlementMap);

    const levelOfCare = (intake.e_levelofcare && intake.e_levelofcare.name) || this.levelOfCareMap[intake.levelofcare_id] || '—';
    const diagnosisStr = (intake.diagnosis ? decryptField(intake.diagnosis) : null) || (intake.e_primarydiagnosis && intake.e_primarydiagnosis.name) || '—';

    let heightStr = '—';
    const hFt = intake.height_feet !== undefined && intake.height_feet !== null ? intake.height_feet : (patient.height_feet !== undefined && patient.height_feet !== null ? patient.height_feet : res.height_feet);
    const hIn = intake.height_inches !== undefined && intake.height_inches !== null ? intake.height_inches : (patient.height_inches !== undefined && patient.height_inches !== null ? patient.height_inches : res.height_inches);
    if ((hFt !== undefined && hFt !== null) || (hIn !== undefined && hIn !== null)) {
      if (Number(hFt) > 0 || Number(hIn) > 0) {
        heightStr = `${hFt || 0}' ${hIn || 0}"`;
      }
    } else {
      const singleHeight = patient.height || intake.height || res.height;
      if (singleHeight) {
        heightStr = String(singleHeight);
      }
    }

    this.patientInfo = {
      name: fullName || '—',
      phone: phone || '—',
      email: patient.email || '—',
      levelOfCare: levelOfCare,
      dateOfBirth: formattedDob,
      age: age !== null ? `${age} years` : '—',
      annualIncome: formattedIncome,
      height: heightStr,
      weight: (patient.weight || intake.weight) ? String(patient.weight || intake.weight) : '—',
      ambulation: this.getLookupName('ambulation', intake.ambulation),
      transfers: this.getLookupName('transfers', intake.transfers),
      bathing: this.getLookupName('bathing', intake.bathing),
      eating: this.getLookupName('eating', intake.eating),
      mentalStatus: this.getLookupName('mentalStatus', intake.mental_status),
      incontinenceBowel: this.getLookupName('incontinencecare', intake.incontinence_care_bowel || intake.incontinence_bowel),
      incontinenceBladder: this.getLookupName('incontinencecare', intake.incontinence_care_bladder || intake.incontinence_bladder),
      diabetesMedication: this.getLookupName('diabetes', intake.diabetes || intake.diabetes_medication),
      dressing: this.getLookupName('dressing', intake.dressing),
      hospiceImminent: this.capitalize(intake.hospice_imminent),
      specialCareNeeds: specialCareNeedsStr,
      dietRestrictions: dietRestrictionsStr,
      possibleEntitlements: entitlementsStr,
      primaryDiagnosis: diagnosisStr,
      medicationNotes: intake.medication_notes ? decryptField(intake.medication_notes) : '—',
      additionalComments: intake.additional_comments ? decryptField(intake.additional_comments) : '—'
    };

    // 3. Agent Information (Source: reservation.e_intakes.e_placementagents)
    const agent = (intake && intake.e_placementagents)
      ? (Array.isArray(intake.e_placementagents) ? intake.e_placementagents[0] : intake.e_placementagents)
      : (res.e_placementagents || {});

    const agentFirstName = agent.first_name || '';
    const agentLastName = agent.last_name || '';
    const agentFullName = [agentFirstName, agentLastName].filter(Boolean).join(' ');
    const agentComp = this.formatCompanyName(agent.company || agent.company_name || agent.companyName);

    this.agentInfo = {
      companyName: agentComp || '—',
      name: agentFullName || agent.name || res.created_by || '—',
      phone: agent.phone || agent.contact_no || '—',
      email: agent.email || '—'
    };

    // 4. Facility Contact Information & Privacy Masking
    const isAdmissionConfirmed = res.is_admission === 1 || res.onboarding_status_id === 4;
    const facility = res.e_facilities || {};

    const facilityContactsArr = facility.e_facilitycontactfacilities || [];
    let facilityContactObj: any = {};
    if (Array.isArray(facilityContactsArr) && facilityContactsArr.length) {
      const firstContactFacility = facilityContactsArr[0] || {};
      facilityContactObj = firstContactFacility.e_facilitycontacts || firstContactFacility.facilitycontacts || firstContactFacility;
    } else if (facility.e_facilitycontacts) {
      facilityContactObj = facility.e_facilitycontacts;
    }

    const contactFirstName = decryptField(facilityContactObj.first_name);
    const contactLastName = decryptField(facilityContactObj.last_name);
    const contactFullName = [contactFirstName, contactLastName].filter(Boolean).join(' ');

    const rawFacilityPhone = decryptField(facilityContactObj.phone);
    const facilityEmail = facilityContactObj.email;

    this.facilityInfo = {
      facilityName: facility.name || '—',
      contactPerson: isAdmissionConfirmed ? (contactFullName || '—') : '*** ***',
      phone: isAdmissionConfirmed ? (rawFacilityPhone || '—') : '(***) ***-****',
      email: isAdmissionConfirmed ? (facilityEmail || '—') : '***@***.***'
    };

    // 5. Reservation Notes
    this.reservationNotes = res.onboarding_notes || '—';
  }

  private mapListItems(itemsArr: any[], idKey: string, mapObj: { [key: number]: string }): string {
    if (!itemsArr || !Array.isArray(itemsArr) || !itemsArr.length) return '—';
    const mapped = itemsArr.map(item => {
      if (!item) return '';
      return mapObj[item[idKey]] || item.name || item.label || '';
    }).filter(Boolean);
    return mapped.length ? mapped.join(', ') : '—';
  }

  private formatCompanyName(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val.name || val.company_name || val.companyName || '';
    return String(val);
  }
  downloadDocument(doc: any, event: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }
    if (!doc) return;

    const fileData = doc.form_fileurl;
    const filename = doc.form_filename || '602A.pdf';
    const mimeType = doc.form_type || 'application/pdf';

    // Handle Buffer type data
    if (fileData && fileData.type === 'Buffer' && Array.isArray(fileData.data)) {
      try {
        const byteArray = new Uint8Array(fileData.data);
        const blob = new Blob([byteArray], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (err) {
        console.error('Error creating blob from buffer:', err);
        this.toastr.error('Failed to download file.');
      }
      return;
    }

    // Fallbacks
    const url = doc.url || doc.file_url || doc.path;
    if (url) {
      window.open(url, '_blank');
      return;
    }

    this.toastr.error('No downloadable file data found.');
  }

  getReservationStatus(statusId: number, statusName?: string): { label: string; color: string } {
    return getReservationStatus(statusId, statusName);
  }

  private getLookupName(listKey: string, val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    if (this.preIntakeData && this.preIntakeData[listKey] && Array.isArray(this.preIntakeData[listKey])) {
      const option = this.preIntakeData[listKey].find(opt => Number(opt.id) === Number(val));
      if (option && option.name) return option.name;
    }
    return this.capitalize(val);
  }

  private capitalize(val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    if (typeof val === 'number') return String(val);
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      const label = val.name || val.label || val.title || val.value;
      if (!label) return '—';
      const strLabel = String(label);
      return strLabel.charAt(0).toUpperCase() + strLabel.slice(1).toLowerCase();
    }
    const str = String(val);
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  fetchReservationNotes(): void {
    if (!this.reservationId) return;
    this.isLoadingNotes = true;
    this.reservationService.getReservationNotes(this.reservationId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isLoadingNotes = false;
          const data = (res && res.data) ? res.data : res;
          this.messages = Array.isArray(data) ? data : [];
        },
        (error) => {
          this.isLoadingNotes = false;
          console.error('Error fetching reservation notes:', error);
          const errorMsg = error && typeof error === 'string' ? error : 'Failed to load reservation notes.';
          sweetAlert("Error", errorMsg, "warning", "OK");
        }
      );
  }

  sendMessage(): void {
    if (!this.newMessageText || !this.newMessageText.trim()) {
      return;
    }
    
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const payload = {
      notes: this.newMessageText.trim()
    };

    this.reservationService.addReservationNote(this.reservationId, payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.newMessageText = '';
          this.fetchReservationNotes();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error adding reservation note:', error);
          const errorMsg = error && typeof error === 'string' ? error : 'Failed to send message.';
          sweetAlert("Error", errorMsg, "warning", "OK");
        }
      );
  }

  isNegotiatedRatesApproved(): boolean {
    return this.reservation &&
           this.reservation.negotiated_total_amount !== null &&
           this.reservation.negotiated_total_amount !== undefined &&
           this.reservation.is_rates_approved === 1;
  }

  calculateTotalCostRange(data: any): string {
    if (!data) return '$0';

    const nights = Number(data.nights || 0);

    const years = Math.floor(nights / 365);
    const months = Math.floor((nights - years * 365) / 30);
    const days = nights - years * 365 - months * 30;

    const totalMonths = (years * 12) + months;
    const stayLengthDays = days;

    const cost = Number(data.cost || 0);
    const costMonthly = Number(data.cost_monthly || 0);

    const minMonth = costMonthly * totalMonths;
    const minNight = cost * stayLengthDays;

    const totalMin = minMonth + minNight;

    const costMax = Number(data.cost_max || 0);
    const costMonthlyMax = Number(data.cost_monthly_max || 0);

    const maxMonth = costMonthlyMax * totalMonths;
    const maxNight = costMax * stayLengthDays;

    const totalMax = maxMonth + maxNight;

    return `$${totalMin} - $${totalMax}`;
  }
}
